/* eslint-disable max-lines */
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../Card';
import { getTimeDisplayFromDate } from '../../../../../../utils';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Form,
} from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFormSchema } from './formSchema';
import { Zeitstempel } from '@/domain/arbeitszeiten/entities';
import { Abweichung } from '../../../Abweichung';
import { useManagerCanEditArbeitszeiten } from '@/domain/arbeitszeiten/hooks/useManagerCanEditArbeitszeit';
import { TimeInput } from '../../../TimeInput';

type BeginnEndeChangeEvent = {
  beginn: string;
  ende: string;
};

// eslint-disable-next-line max-lines-per-function, complexity
export function BeginnEnde({
  beginn,
  ende,
  kommen,
  gehen,
  onChange,
  disabled = false,
}: {
  beginn: Zeitstempel | null;
  ende: Zeitstempel | null;
  kommen: Zeitstempel | null;
  gehen: Zeitstempel | null;
  disabled?: boolean;
  onChange?: (event: BeginnEndeChangeEvent) => void;
}) {
  const { t } = useTranslation();
  const canEditArbeitszeit = useManagerCanEditArbeitszeiten();

  const beginnNachPlan = beginn.schicht ? beginn.schicht.beginn.toDate() : null;
  const endeNachPlan = ende.schicht ? ende.schicht.ende.toDate() : null;

  const form = useForm<{
    beginn: string;
    ende: string;
    kommen: string;
    gehen: string;
    planBeginn: string;
    planEnde: string;
  }>({
    defaultValues: {
      beginn:
        beginn && beginn.zeitpunkt !== null
          ? getTimeDisplayFromDate(beginn.zeitpunkt)
          : '',
      ende:
        ende && ende.zeitpunkt !== null
          ? getTimeDisplayFromDate(ende.zeitpunkt)
          : '',
      kommen:
        kommen && kommen.zeitpunkt !== null
          ? getTimeDisplayFromDate(kommen.zeitpunkt)
          : '',
      gehen:
        gehen && gehen.zeitpunkt !== null
          ? getTimeDisplayFromDate(gehen.zeitpunkt)
          : '',
      planBeginn:
        beginnNachPlan !== null ? getTimeDisplayFromDate(beginnNachPlan) : '',
      planEnde:
        endeNachPlan !== null ? getTimeDisplayFromDate(endeNachPlan) : '',
    },
    resolver: zodResolver(useFormSchema()),
  });

  function getTimeStringOrPlaceholder(value: Date | undefined) {
    return !value ? '--:--' : getTimeDisplayFromDate(value);
  }

  function onSubmit({ beginn, ende }: { beginn?: string; ende?: string }) {
    const currentBeginn = beginn ?? form.getValues('beginn');
    const currentEnde = ende ?? form.getValues('ende');
    onChange({ beginn: currentBeginn, ende: currentEnde });
  }
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col md:flex-row gap-1 md:gap-1 justify-between items-center">
          <Card>
            <div className="flex flex-col gap-1 ">
              <FormField
                control={form.control}
                name="beginn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('domain.arbeitszeiten.sheet.arbeitszeit.beginnLabel')}
                    </FormLabel>
                    <FormControl>
                      <TimeInput
                        disabled={disabled}
                        onBlur={(e) => {
                          const value = e.currentTarget.value;
                          field.onChange(value);
                          form.handleSubmit(onSubmit)();
                        }}
                        placeholder="--:--"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormDescription />
                  </FormItem>
                )}
              />
              <Abweichung
                abweichung={
                  beginn && beginn.abweichung !== null ? beginn.abweichung : 0
                }
              />
            </div>
            <div className="flex flex-col gap-1 ">
              <FormField
                control={form.control}
                name="kommen"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('domain.arbeitszeiten.sheet.arbeitszeit.kommenLabel')}
                    </FormLabel>
                    <FormControl>
                      <TimeInput
                        disabled
                        applyValue={
                          kommen &&
                          kommen.zeitpunkt &&
                          kommen.zeitpunkt?.getTime() !==
                            (beginn?.zeitpunkt?.getTime() ?? null)
                        }
                        placeholder={getTimeStringOrPlaceholder(
                          kommen?.zeitpunkt,
                        )}
                        onClick={() => {
                          const value = getTimeDisplayFromDate(
                            kommen?.zeitpunkt,
                          );
                          form.setValue('beginn', value);
                          field.onChange(value);
                          form.handleSubmit(onSubmit)();
                        }}
                      />
                    </FormControl>
                    <FormDescription />
                  </FormItem>
                )}
              />
              <Abweichung
                abweichung={
                  kommen && kommen.abweichung !== null ? kommen.abweichung : 0
                }
              />
            </div>
            <FormField
              control={form.control}
              name="planBeginn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('domain.arbeitszeiten.sheet.arbeitszeit.planLabel')}
                  </FormLabel>
                  <FormControl>
                    <TimeInput
                      disabled
                      schichtUpdate={canEditArbeitszeit}
                      applyValue={
                        beginnNachPlan !== null &&
                        beginnNachPlan.getTime() !== beginn.zeitpunkt?.getTime()
                      }
                      placeholder={getTimeStringOrPlaceholder(
                        beginnNachPlan
                          ? beginnNachPlan
                          : endeNachPlan
                          ? beginn.zeitpunkt
                          : null,
                      )}
                      onClick={() => {
                        const value = getTimeDisplayFromDate(beginnNachPlan);
                        form.setValue('beginn', value);
                        field.onChange(value);
                        form.handleSubmit(onSubmit)();
                      }}
                    />
                  </FormControl>
                  <FormDescription />
                </FormItem>
              )}
            />
          </Card>
          <div className="hidden md:inline md:h-[1px] md:bg-neutrals-N500 md:w-32" />
          <div className="w-[1px] bg-neutrals-N500 h-8 md:hidden" />
          <Card>
            <div className="flex flex-col gap-1 ">
              <FormField
                control={form.control}
                name="ende"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('domain.arbeitszeiten.sheet.arbeitszeit.endeLabel')}
                    </FormLabel>
                    <FormControl>
                      <TimeInput
                        disabled={disabled}
                        onBlur={(e) => {
                          const value = e.currentTarget.value;
                          field.onChange(value);
                          form.handleSubmit(onSubmit)();
                        }}
                        onChange={field.onChange}
                        value={field.value}
                        placeholder="--:--"
                      />
                    </FormControl>
                    <FormDescription />
                  </FormItem>
                )}
              />
              <Abweichung
                abweichung={
                  ende && ende.abweichung !== null ? ende.abweichung : 0
                }
              />
            </div>
            <div className="flex flex-col gap-1 ">
              <FormField
                control={form.control}
                name="gehen"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('domain.arbeitszeiten.sheet.arbeitszeit.gehenLabel')}
                    </FormLabel>
                    <FormControl>
                      <TimeInput
                        disabled
                        className="text-primary-blue-B600 cursor-pointer"
                        placeholder={getTimeStringOrPlaceholder(
                          gehen?.zeitpunkt,
                        )}
                        applyValue={
                          gehen &&
                          gehen.zeitpunkt &&
                          gehen.zeitpunkt?.getTime() !==
                            (ende?.zeitpunkt?.getTime() ?? null)
                        }
                        onClick={() => {
                          const value = getTimeDisplayFromDate(
                            gehen?.zeitpunkt,
                          );
                          form.setValue('ende', value);
                          field.onChange(value);
                          form.handleSubmit(onSubmit)();
                        }}
                      />
                    </FormControl>
                    <FormDescription />
                  </FormItem>
                )}
              />
              <Abweichung
                abweichung={
                  gehen && gehen.abweichung !== null ? gehen.abweichung : 0
                }
              />
            </div>
            <FormField
              control={form.control}
              name="planEnde"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('domain.arbeitszeiten.sheet.arbeitszeit.planLabel')}
                  </FormLabel>
                  <FormControl>
                    <TimeInput
                      disabled
                      schichtUpdate={canEditArbeitszeit}
                      applyValue={
                        endeNachPlan !== null &&
                        endeNachPlan.getTime() !== ende.zeitpunkt?.getTime()
                      }
                      onClick={() => {
                        const value = getTimeDisplayFromDate(endeNachPlan);
                        form.setValue('ende', value);
                        field.onChange(value);
                        form.handleSubmit(onSubmit)();
                      }}
                      placeholder={getTimeStringOrPlaceholder(
                        endeNachPlan
                          ? endeNachPlan
                          : beginnNachPlan
                          ? ende.zeitpunkt
                          : null,
                      )}
                    />
                  </FormControl>
                  <FormDescription />
                </FormItem>
              )}
            />
          </Card>
        </div>
        {Object.keys(form.formState.errors).length > 0 && (
          <div className="mt-4">
            {Object.values(form.formState.errors).map((error, index) => (
              <FormMessage
                key={index}
                className="bg-red-100 rounded-sm text-error-main p-4 text-sm font-semibold">
                {error.message}
              </FormMessage>
            ))}
          </div>
        )}
      </form>
    </Form>
  );
}
